import { Formik, Form } from 'formik'
import Grid from '../../../../../elements/Grid'
import CustomField from '../../../../../elements/form/CustomField'
import Button from '../../../../../elements/Button'
import styles from './styles.module.scss'
import { useResponsive } from '../../../../../../providers/ResponsiveContext'
import * as Yup from 'yup'
import { useState, useEffect } from 'react'
import axios from '/components/configs/axios'
import { useModalContext } from '../../../../../../providers/modalProvider'
import Message from '../../../../../custom/Message'
import { useRouter } from 'next/router'
import {
  getUserContact,
  saveUserContact
} from '/components/helpers/userContactStorage'

const ContactForm = ({
  variant = [],
  route = 'email/send',
  disabled,
  thankYou,
  translations,
  ...props
}) => {
  const router = useRouter()
  const { isSmallMobile } = useResponsive()
  const [savedContact, setSavedContact] = useState({})
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Load saved contact info on mount
  useEffect(() => {
    const contact = getUserContact()
    setSavedContact(contact)
    if (contact.phone) {
      setPhone(contact.phone)
    }
  }, [])

  const formInitialValues = {
    firstName: savedContact.firstName || '',
    lastName: savedContact.lastName || '',
    email: savedContact.email || '',
    phone: savedContact.phone || '',
    message: '',
    page: router.asPath
  }

  const { handleShowModal, setContent, setVariant } = useModalContext()

  const SignupSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredFirstName[router.locale]),
    lastName: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        translations.form.error.validName[router.locale]
      )
      .required(translations.form.error.requiredLastName[router.locale]),
    email: Yup.string()
      .max(254)
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        translations.form.error.validEmail[router.locale]
      )
      .email(translations.form.error.validEmail[router.locale])
      .required(translations.form.error.requiredEmail[router.locale]),
    phone: Yup.string().max(20)
  })

  // Handler to save contact info on field blur
  const handleFieldBlur = (fieldName, value) => {
    if (value) {
      saveUserContact({ [fieldName]: value })
    }
  }

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true)

    // Save contact info on submit
    saveUserContact({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone
    })

    axios
      .post(route, { ...values, ...props })
      .then(() => {
        axios
          .post('active/pipe', {
            ...values,
            ...props
          })
          .catch((err) => console.error(err))

        setSubmitted(true)
        setVariant(['contained', 'xs'])
        setContent(
          <Message
            title={translations.form.success.title[router.locale]}
            subtitle={translations.form.success.subtitle[router.locale]}
            button={{
              label: translations.form.success.button[router.locale],
              href: thankYou?.url || '/vacation-rentals',
              arrow: false
            }}
          />
        )
        handleShowModal()
        resetForm()
        setPhone('')
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <div>
      <Formik
        validationSchema={SignupSchema}
        initialValues={formInitialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, setFieldValue }) => {
          const fieldVariant = variant.includes('dark')
            ? ['full-width']
            : ['gray', 'underline', 'full-width']

          return (
            <Form>
              <Grid variant={[isSmallMobile ? 'grid-1' : 'grid-2', 'rows-xs']}>
                <CustomField
                  isActive={values.firstName}
                  disabled={disabled}
                  required
                  name="firstName"
                  placeholder={
                    translations.form.fields.firstName[router.locale]
                  }
                  maxLength={20}
                  onChange={(e) => {
                    setFieldValue(
                      'firstName',
                      e.target.value.replace(/[0-9]/g, '')
                    )
                  }}
                  onBlur={(e) => handleFieldBlur('firstName', e.target.value)}
                  variant={fieldVariant}
                  error={errors.firstName && touched?.firstName}
                />

                <CustomField
                  disabled={disabled}
                  required
                  maxLength={20}
                  isActive={values.lastName}
                  name="lastName"
                  placeholder={translations.form.fields.lastName[router.locale]}
                  variant={fieldVariant}
                  onChange={(e) => {
                    setFieldValue(
                      'lastName',
                      e.target.value.replace(/[0-9]/g, '')
                    )
                  }}
                  onBlur={(e) => handleFieldBlur('lastName', e.target.value)}
                  error={errors.lastName && touched.lastName}
                />

                <CustomField
                  disabled={disabled}
                  required
                  name="email"
                  type="email"
                  isActive={values.email}
                  maxLength={254}
                  placeholder={translations.form.fields.email[router.locale]}
                  variant={fieldVariant}
                  onBlur={(e) => handleFieldBlur('email', e.target.value)}
                  error={errors.email && touched.email}
                  valid={values.email && !errors.email && touched.email}
                />

                <CustomField
                  disabled={disabled}
                  name="phone"
                  maxLength={20}
                  optional
                  isActive={values.phone}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(
                      /[^0-9+()]/g,
                      ''
                    )
                    setFieldValue('phone', cleanedValue)
                    setPhone(cleanedValue)
                  }}
                  onBlur={(e) => handleFieldBlur('phone', e.target.value)}
                  value={phone}
                  type="tel"
                  placeholder={translations.form.fields.phone[router.locale]}
                  variant={fieldVariant}
                />
              </Grid>

              <CustomField
                disabled={disabled}
                name="message"
                className={styles.message}
                optional
                type="textarea"
                as="textarea"
                rows={9}
                label={translations.form.fields.message[router.locale]}
                variant={['gray', 'full-width']}
              />

              <Button
                disabled={
                  errors.firstName ||
                  errors.lastName ||
                  errors.email ||
                  submitted ||
                  disabled
                }
                variant={[!variant.includes('dark') && 'primary-blue', 'xl']}
              >
                {translations.form.fields.submit[router.locale]}
              </Button>

              <div className={styles.terms}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: translations.form.agreement[router.locale]
                  }}
                ></p>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default ContactForm
